import { create } from 'zustand';
import type { Word, Rating, SessionSummary } from '../types';

interface StudyState {
  queue: Word[];
  currentIndex: number;
  isFlipped: boolean;
  sessionStartTime: number | null;
  /** Word ids already re-queued once this session (failed cards return once). */
  requeuedIds: string[];
  sessionStats: {
    wordsReviewed: number;
    ratings: Record<Rating, number>;
    xpEarned: number;
    startTime: number;
  };
  summary: SessionSummary | null;

  setQueue: (queue: Word[]) => void;
  nextCard: () => void;
  prevCard: () => void;
  flipCard: () => void;
  submitRating: (rating: Rating, xp: number) => void;
  /** Push the current card to the end of the queue so a failed word is seen again this session. */
  requeueCurrent: () => boolean;
  /** Drop the current card from the queue (e.g. after "I know this"). */
  removeCurrent: () => void;
  resetSession: () => void;
  setSummary: (summary: SessionSummary) => void;
}

const emptyStats = () => ({
  wordsReviewed: 0,
  ratings: { again: 0, hard: 0, good: 0, easy: 0 } as Record<Rating, number>,
  xpEarned: 0,
  startTime: Date.now(),
});

export const useStudyStore = create<StudyState>((set, get) => ({
  queue: [],
  currentIndex: 0,
  isFlipped: false,
  sessionStartTime: null,
  requeuedIds: [],
  sessionStats: emptyStats(),
  summary: null,

  setQueue: (queue) =>
    set({
      queue,
      currentIndex: 0,
      isFlipped: false,
      sessionStartTime: Date.now(),
      requeuedIds: [],
      sessionStats: emptyStats(),
      summary: null,
    }),

  nextCard: () =>
    set((state) => ({
      currentIndex: Math.min(state.currentIndex + 1, state.queue.length),
      isFlipped: false,
    })),

  prevCard: () =>
    set((state) => ({
      currentIndex: Math.max(state.currentIndex - 1, 0),
      isFlipped: false,
    })),

  flipCard: () => set((state) => ({ isFlipped: !state.isFlipped })),

  submitRating: (rating, xp) =>
    set((state) => ({
      sessionStats: {
        ...state.sessionStats,
        wordsReviewed: state.sessionStats.wordsReviewed + 1,
        ratings: {
          ...state.sessionStats.ratings,
          [rating]: state.sessionStats.ratings[rating] + 1,
        },
        xpEarned: state.sessionStats.xpEarned + xp,
      },
    })),

  requeueCurrent: () => {
    const { queue, currentIndex, requeuedIds } = get();
    const word = queue[currentIndex];
    if (!word || requeuedIds.includes(word.id)) return false;
    set({
      queue: [...queue, word],
      requeuedIds: [...requeuedIds, word.id],
    });
    return true;
  },

  removeCurrent: () => {
    const { queue, currentIndex } = get();
    if (!queue[currentIndex]) return;
    const next = queue.filter((_, i) => i !== currentIndex);
    set({
      queue: next,
      currentIndex: Math.min(currentIndex, Math.max(next.length - 1, 0)),
      isFlipped: false,
    });
  },

  resetSession: () =>
    set({
      queue: [],
      currentIndex: 0,
      isFlipped: false,
      sessionStartTime: null,
      requeuedIds: [],
      sessionStats: emptyStats(),
      summary: null,
    }),

  setSummary: (summary) => set({ summary }),
}));
